import type { User } from 'firebase/auth';

type FirebaseDatabaseUser = Pick<User, 'getIdToken' | 'getIdTokenResult'>;

interface GuestSession {
  access_token: string;
}

interface GuestAuthClient {
  auth: {
    getSession: () => Promise<{
      data: { session: GuestSession | null };
      error: unknown;
    }>;
    signInAnonymously: (credentials?: {
      options?: { captchaToken?: string };
    }) => Promise<{
      data: {
        session: GuestSession | null;
        user: { is_anonymous?: boolean } | null;
      };
      error: unknown;
    }>;
    signOut: (options: { scope: 'local' }) => Promise<{ error: unknown }>;
  };
}

export class DatabaseIdentityController {
  private guestAccessTokenPromise: Promise<string> | null = null;
  private activeFirebaseUser: FirebaseDatabaseUser | null | undefined;
  private identitySequence = 0;

  constructor(
    private readonly guestAuthClient: GuestAuthClient,
    private readonly getCaptchaToken?: () => Promise<string>
  ) {}

  private isCaptchaError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
      return false;
    }
    const message = 'message' in error ? String(error.message).toLowerCase() : '';
    const code = 'code' in error ? String(error.code).toLowerCase() : '';
    return message.includes('captcha') || code.includes('captcha');
  }

  private async signInGuest() {
    const firstAttempt = await this.guestAuthClient.auth.signInAnonymously();
    if (!firstAttempt.error) {
      return firstAttempt;
    }
    if (!this.isCaptchaError(firstAttempt.error) || !this.getCaptchaToken) {
      throw firstAttempt.error;
    }

    const captchaToken = await this.getCaptchaToken();
    const verifiedAttempt = await this.guestAuthClient.auth.signInAnonymously({
      options: { captchaToken },
    });
    if (verifiedAttempt.error) {
      throw verifiedAttempt.error;
    }
    return verifiedAttempt;
  }

  private async createGuestAccessToken(): Promise<string> {
    const { data: sessionData, error: sessionError } = await this.guestAuthClient.auth.getSession();
    if (sessionError) {
      throw sessionError;
    }

    if (sessionData.session?.access_token) {
      return sessionData.session.access_token;
    }

    const { data } = await this.signInGuest();
    if (!data.session?.access_token || !data.user?.is_anonymous) {
      throw new Error('Supabase anonymous sign-in returned an invalid session');
    }

    return data.session.access_token;
  }

  private async getGuestAccessToken(): Promise<string> {
    if (!this.guestAccessTokenPromise) {
      this.guestAccessTokenPromise = this.createGuestAccessToken().finally(() => {
        this.guestAccessTokenPromise = null;
      });
    }
    return this.guestAccessTokenPromise;
  }

  async getAccessToken(): Promise<string> {
    const sequence = this.identitySequence;
    const firebaseUser = this.activeFirebaseUser;
    if (firebaseUser === undefined) {
      throw new Error('Database identity synchronization is in progress');
    }

    const accessToken = firebaseUser
      ? await firebaseUser.getIdToken(false)
      : await this.getGuestAccessToken();
    if (sequence !== this.identitySequence || firebaseUser !== this.activeFirebaseUser) {
      throw new Error('Database identity changed while obtaining an access token');
    }
    return accessToken;
  }

  async synchronize(user: FirebaseDatabaseUser | null): Promise<void> {
    const sequence = ++this.identitySequence;
    this.activeFirebaseUser = undefined;

    if (!user) {
      await this.getGuestAccessToken();
      if (sequence === this.identitySequence) {
        this.activeFirebaseUser = null;
      }
      return;
    }

    const { data: sessionData, error: sessionError } = await this.guestAuthClient.auth.getSession();
    if (sessionError) {
      throw sessionError;
    }
    if (sessionData.session) {
      const { error } = await this.guestAuthClient.auth.signOut({ scope: 'local' });
      if (error) {
        throw error;
      }
    }

    const token = await user.getIdTokenResult(true);
    if (sequence !== this.identitySequence) {
      return;
    }
    if (token.claims.role !== 'authenticated') {
      throw new Error('Firebase user is missing the authenticated database role');
    }
    this.activeFirebaseUser = user;
  }
}
